import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import { CircularProgress, Link, TextField, WithStyles } from '@material-ui/core';
import { Domain } from '@material-ui/icons';
import { createStyles, withStyles } from '@material-ui/styles';
import { Autocomplete, Stack } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import 'moment/locale/it';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import ReduxLanguage from '../../components/ReduxLanguage';
import MksRelationsFilterBar from '../../components/SearchBar/MksRelationsFilterBar';
import * as MksActions from '../../redux/mks/mks.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { IState } from '../../redux/store';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IKeyInDTO } from '../../repository/jago/model/input/IKeyInDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import { IMechanicalCylinderKeyRelationsRequestParamsDTO, IWalletPlantsRequestParamsDTO, IMksImportsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { getDomainsOptions, getDomainNameFromHostname } from '../../repository/utils';
import translations from '../../translations/i18next';
import { ModalTypes } from '../../components/Modals/ModalTypes';
import MksCylinderForm from '../../components/Forms/MksCylinderForm';
import * as ModalsActions from '../../redux/modals/modals.actions';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  text: {
    margin: 0,
    marginBottom: 15,
  },
  logo: {
    width: '20%',
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 170px)',
    width: '100%',
    overflow: 'auto'
  },
});

export interface IMksRelationsMatrixPageState {
  domainsOptions: IDomainOptionDTO[];
  isLoading: boolean;
  domainSearchText: string;
  matrixData: any[];
  matrixColumns: GridColumns;
}

type IReduxProps = ConnectedProps<typeof connector>;

export interface IMksRelationsMatrixPageProps extends WithStyles<typeof styles> {
  language: string;
  relations: PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO>;
  cylinders: PaginatedDataDTO<ICylinderInDTO>;
  keys: PaginatedDataDTO<IKeyInDTO>;
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  lastImport: IMksImportInDTO | {};
}

export type ComponentProps = IMksRelationsMatrixPageProps & IReduxProps;

class MksRelationsMatrixPage extends React.Component<ComponentProps, IMksRelationsMatrixPageState> {
  
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      domainsOptions: [],
      isLoading: false,
      domainSearchText: '',
      matrixData: [],
      matrixColumns: [],
    }
  }

  public async componentDidMount() {
    await this.fetchDomainsOptions('');
    
    const { selectedDomain, dispatch, relations } = this.props;
    const hasRelationsData = relations?.content && relations.content.length > 0;
    
    try {
      this.setState({ isLoading: true });
      if (selectedDomain) {
        if (!hasRelationsData) {
          await this.fetchRelationsForSelectedDomain();
        } else {
          setTimeout(() => {
            this.buildMatrixFromRelations(relations.content || []);
            this.setState({ isLoading: false });
          }, 200);
        }
        //Fetch keys and cylinders for selected domain
        await dispatch<any>(MksActions.fetchKeys({ hostname: selectedDomain.value.hostname, page: 0, pageSize: 200 }));
        await dispatch<any>(MksActions.fetchCylinders({ hostname: selectedDomain.value.hostname, page: 0, pageSize: 200 }));
      } else {

        const savedHostname = localStorage.getItem('masterKeyDomain');
        if (savedHostname) {
          const params: IWalletPlantsRequestParamsDTO = { 
            hostname: savedHostname,
            page: 0,
            pageSize: 50,
            walletId: undefined
          };
          const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
          if (domainsResponse && domainsResponse.length > 0) {
            const domainsOptions = getDomainsOptions(domainsResponse);
            if (domainsOptions && domainsOptions.length > 0) {
              const domainOption = domainsOptions[0];
              dispatch<any>(MksActions.setSelectedDomain(domainOption));
              this.setState({ domainSearchText: domainOption.label, domainsOptions }, async () => {
                if (!hasRelationsData) {
                  await this.fetchRelationsForSelectedDomain();
                } else {
                  this.buildMatrixFromRelations(relations.content || []);
                  this.setState({ isLoading: false });
                }
              });
            }
            await dispatch<any>(MksActions.fetchKeys({ hostname: savedHostname, page: 0, pageSize: 200 }));
            await dispatch<any>(MksActions.fetchCylinders({ hostname: savedHostname, page: 0, pageSize: 200 }));
          } else {
            this.setState({ isLoading: false });
          }
        } else {
          this.setState({ isLoading: false });
        }
      }
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  private async fetchRelationsForSelectedDomain() {
    const { selectedDomain, dispatch } = this.props;
    if (!selectedDomain) return;
    const params: IMechanicalCylinderKeyRelationsRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 5000,
    };

    dispatch<any>(MksActions.resetMksRelationsData());
    await this.onSearch(params);
    await this.fetchLastImport();
  }

  public async fetchDomainsOptions(searchText: string) {
    const { dispatch } = this.props;
    try {
      const params: IWalletPlantsRequestParamsDTO = { 
        hostname: searchText,
        page: 0,
        pageSize: 50,
        walletId: undefined
      };
      const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
      if (domainsResponse) {
        const domainsOptions = getDomainsOptions(domainsResponse);
        this.setState({ domainsOptions });
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  }

  public async onSearch(params: IMechanicalCylinderKeyRelationsRequestParamsDTO) {
    const { dispatch } = this.props;
    try {
      this.setState({ isLoading: true });
      const result = await dispatch<any>(MksActions.fetchMechanicalCylinderKeyRelations(params));
      if (result && result.content) {
        this.buildMatrixFromRelations(result.content);
      }
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  private buildMatrixFromRelations(relations: IMechanicalCylinderKeyRelationInDTO[]) {
    const uniqueCylinders = _.uniqBy(
      relations.map(r => r.cylinder).filter(c => c),
      'id'
    );
    
    const uniqueKeys = _.uniqBy(
      relations.map(r => r.key).filter(k => k),
      'id'
    );

    

    const relationsMap = new Map<string, boolean>();
    relations.forEach(rel => {
      const key = `${rel.cylinderId}_${rel.keyId}`;
      relationsMap.set(key, true);
    });

    const matrixData = uniqueCylinders.map((cylinder) => {
      const row: any = {
        id: cylinder.id,
        cylinderName: cylinder.doorName || `Cylinder ${cylinder.id}`,
        cylinderData: cylinder,
      };
      
      uniqueKeys.forEach((key) => {
        const hasRelation = relationsMap.has(`${cylinder.id}_${key.id}`);
        row[`key_${key.id}`] = hasRelation ? '•' : '';
      });
      
      return row;
    });

    const matrixColumns: GridColumns = [
      {
        field: 'cylinderName',
        headerName: translations.t('mks.cylinderName'),
        width: 200,
        sortable: true,
        headerClassName: 'sticky-column-header',
        cellClassName: 'sticky-column-cell',
        renderCell: (params: GridRenderCellParams<any>) => {
          return <div style={{ fontWeight: 'bold', fontSize: 11 }}>{params.value}</div>
        }
      },
      ...uniqueKeys.map((key) => ({
        field: `key_${key.id}`,
        headerName: key.keyFunction || `Key ${key.id}`,
        width: 80,
        sortable: false,
        filterable: false,
        align: 'center' as const,
        headerAlign: 'center' as const,
        renderCell: (params: GridRenderCellParams<any>) => {
          return <div style={{ fontSize: 16, fontWeight: 'bold' }}>{params.value}</div>
        }
      }))
    ];

    this.setState({ matrixData, matrixColumns });
  }

  private async fetchLastImport() {
    const { dispatch, selectedDomain } = this.props;
    if (!selectedDomain) return;

    try {
      const params: IMksImportsRequestParamsDTO = {
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 1,
      };
      await dispatch<any>(MksActions.fetchLastMksImport(params));
    } catch (error) {
      // Handle error silently
    }
  }

  public onResetFilters() {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(null));
    localStorage.removeItem('masterKeyDomain');
    this.setState({ domainSearchText: '', matrixData: [], matrixColumns: [] });
  }

  public onDomainSelect(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(domain));
    
    if (!domain) {
      this.setState({ domainSearchText: '', matrixData: [], matrixColumns: [] });
      localStorage.removeItem('masterKeyDomain');
    } else {
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, () => {
        dispatch<any>(MksActions.resetMksRelationsData());
        dispatch<any>(MksActions.fetchMksDataForSelectedDomain());
      });
    }
  }

  public onSelectDomainFromEmptyState(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    
    if (domain) {
      dispatch<any>(MksActions.setSelectedDomain(domain));
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, async () => {
        this.setState({ isLoading: true });
        dispatch<any>(MksActions.resetMksRelationsData());
        await dispatch<any>(MksActions.fetchMksDataForSelectedDomain());
        const { relations } = this.props;
        this.buildMatrixFromRelations(relations.content || []);
        this.setState({ isLoading: false });
      });
    } else {
      localStorage.removeItem('masterKeyDomain');
      this.setState({ domainSearchText: '', matrixData: [], matrixColumns: [] });
    }
  }

  public onFetchDomainsFromEmptyState(value: string) {
    this.fetchDomainsOptions(value);
  }

  public onRowClick(relationData: any) {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedCylinder(relationData.cylinderData));
    dispatch<any>(ModalsActions.showModal(`MKS_CYLINDER_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: <MksCylinderForm cylinderData={relationData.cylinderData} />,
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_CYLINDER_DETAILS_MODAL`)),
      }
    }));
  }

  render() {
    const { classes, selectedHostname, selectedDomain, cylinders, keys } = this.props;
    const { domainsOptions, domainSearchText, matrixData, matrixColumns } = this.state;

    const cylindersArray = (cylinders?.content || []) as ICylinderInDTO[];
    const keysArray = (keys?.content || []) as IKeyInDTO[];

    return (
      <div className={classes.container}>
        <div className={classes.header} style={{ backgroundColor: 'white' }}>
          <MksRelationsFilterBar
            onSearch={(params) => this.onSearch(params)}
            onResetFilters={() => this.onResetFilters()}
            onFilterDomains={(value) => this.fetchDomainsOptions(value)}
            onDomainSelect={(domain) => this.onDomainSelect(domain)}
            domainsOptions={domainsOptions}
            selectedHostname={selectedHostname}
            selectedDomain={selectedDomain}
            isVisible={!!selectedHostname}
            lastImport={this.props.lastImport}
            cylinders={cylindersArray}
            keys={keysArray}
          />
        </div>
        <div className={classes.tableContainer}>
          {selectedHostname ? (
            <DataGrid
              getRowId={(d) => d.id}
              rows={matrixData}
              columns={matrixColumns}
              rowHeight={35}
              loading={this.state.isLoading}
              hideFooter={true}
              onRowClick={(params) => this.onRowClick(params.row)}
              disableColumnMenu={false}
              disableVirtualization
              columnBuffer={1000}
              sx={{
                width: '100%',
                height: '100%',
                '& .MuiDataGrid-main': {
                  overflow: 'unset',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto !important',
                  marginTop: '0px !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  minHeight: '100px !important',
                  marginTop: '0px !important',
                },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                  position: 'relative',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader': {
                  fontSize: 10,
                  fontWeight: 'bold',
                  backgroundColor: 'white',
                },
                '& .MuiDataGrid-row': {
                  fontSize: 11,
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white',
                  zIndex: 3,
                },
                '& .MuiDataGrid-columnHeader[data-field="cylinderName"]': {
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'white',
                  zIndex: 4,
                  borderRight: '2px solid #e0e0e0',
                  boxShadow: '2px 0px 5px rgba(0,0,0,0.1)',
                },
                '& .MuiDataGrid-cell[data-field="cylinderName"]': {
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'white',
                  zIndex: 2,
                  borderRight: '2px solid #e0e0e0',
                  boxShadow: '2px 0px 5px rgba(0,0,0,0.08)',
                },
              }}
              components={{
                NoRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                      <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                        <ReduxLanguage languageKey='mks.noRelationsFoundTitle' />
                      </h3>
                      <h4 style={{ textAlign: 'center', marginTop: 0 }}>
                        <ReduxLanguage languageKey='mks.noRelationsFoundDescription' />
                      </h4>
                    </div>
                  </Stack>
                ),
              }}
            />
          ) : (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              {this.state.isLoading ? (
                <CircularProgress style={{ marginBottom: 24 }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                    <Domain style={{ fontSize: 40, marginBottom: 10 }} />
                    <h2 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                      <ReduxLanguage languageKey='mks.hostnameRequiredTitle' />
                    </h2>
                    <h3 style={{ textAlign: 'center', marginTop: 10, marginBottom: '24px' }}>
                      <ReduxLanguage languageKey='mks.hostnameRequiredDescription' />
                    </h3>
                    <Autocomplete
                      options={domainsOptions}
                      value={selectedDomain || null}
                      inputValue={selectedDomain ? selectedDomain.label : domainSearchText}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (option.label) return option.label;
                        if (option.value?.name) return option.value.name;
                        if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
                        return '';
                      }}
                      isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          variant="outlined" 
                          label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                          required
                          error={!selectedDomain}
                          helperText={''}
                          style={{ minWidth: 300 }}
                        />
                      )}
                      onInputChange={(event, newInputValue) => { 
                        this.setState({ domainSearchText: newInputValue }); 
                        const debouncedDomainSearch = _.debounce((text) => this.onFetchDomainsFromEmptyState(text), 500);
                        debouncedDomainSearch(newInputValue); 
                      }}
                      onChange={(event, newValue) => this.onSelectDomainFromEmptyState(newValue)}
                      noOptionsText="No domains found"
                      loadingText="Loading domains..."
                      clearOnBlur={false}
                      clearOnEscape={true}
                    />
                    <h3 style={{ marginTop: 20 }}>
                      <Link href="https://help-center.sofialocks.com/vasho-master-key-systems-management/" target="_blank" style={{ textDecoration: 'underline' }}>
                        {translations.t('forms.findOutMoreAboutThisFeature')}
                      </Link>
                    </h3>
                  </div>
                </div>
              )}
            </Stack>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    relations: state.mks.relations.data,
    cylinders: state.mks.cylinders.data,
    keys: state.mks.keys.data,
    selectedHostname: state.mks.selectedHostname,
    selectedDomain: state.mks.selectedDomain,
    lastImport: state.mks.imports.lastImport,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(MksRelationsMatrixPage as any));

